import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { managedEntities } from '~/services/serviceInstances';

export type ManagedEntitiesBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const managedEntitiesBaseQuery: BaseQueryFn<
  ManagedEntitiesBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
    // indexes: null → repeat format (key=v1&key=v2) instead of Axios default
    // bracket format (key[]=v1), which FastAPI List params require.
    paramsSerializer: { indexes: null },
  };

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await managedEntities.get(path, config);
    } else if (m === 'post') {
      result = await managedEntities.post(path, body, config);
    } else if (m === 'patch') {
      result = await managedEntities.patch(path, body, config);
    } else if (m === 'put') {
      result = await managedEntities.put(path, body, config);
    } else if (m === 'delete') {
      result = await managedEntities.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (e) {
    if (isAxiosError(e)) {
      return {
        error: {
          status: e.response?.status ?? 'FETCH_ERROR',
          data: e.response?.data ?? e.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(e) } };
  }
};
